/** Theme controller — System (default, follows the OS) / Light / Dark.
 *
 * A single `<html class="dark">` toggle is the source of truth (see styles/tokens.css). The user's
 * explicit choice persists to localStorage; "system" re-resolves live as the OS preference changes.
 * An inline script in index.html applies the same logic BEFORE first paint (no flash); this hook keeps
 * React in sync and re-applies on change. SSR/no-DOM safe (all DOM/storage access is guarded).
 */
import { useCallback, useEffect, useState } from "react";

export type ThemeMode = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "prisma.theme";

function systemPrefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    !!window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

export function readMode(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    /* localStorage unavailable — fall back to system */
  }
  return "system";
}

function resolve(mode: ThemeMode): ResolvedTheme {
  return mode === "system" ? (systemPrefersDark() ? "dark" : "light") : mode;
}

function applyResolved(resolved: ResolvedTheme): void {
  if (typeof document === "undefined") return;
  const el = document.documentElement;
  el.classList.toggle("dark", resolved === "dark");
  el.style.colorScheme = resolved;
}

export function useTheme() {
  const [mode, setModeState] = useState<ThemeMode>(() => readMode());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolve(readMode()));

  // Apply whenever the mode changes (and on mount), keeping `resolved` in sync.
  useEffect(() => {
    const r = resolve(mode);
    applyResolved(r);
    setResolved(r);
  }, [mode]);

  // While in "system" mode, follow live OS changes.
  useEffect(() => {
    if (mode !== "system" || typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      const r = systemPrefersDark() ? "dark" : "light";
      applyResolved(r);
      setResolved(r);
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore persistence failure — the in-memory choice still applies for this session */
    }
    setModeState(next);
  }, []);

  return { mode, setMode, resolved };
}
